import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Building, DollarSign, Calendar, MapPin, Clock, AlertTriangle, MessageSquare, Star, CheckCircle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRFQ } from '@/hooks/useRFQs';
import { formatCurrency, JOB_CATEGORIES } from '@/lib/types/job';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { usePublicRFQCriteria } from '@/hooks/usePublicRFQCriteria';
import { BidSubmission } from '@/components/jobs/BidSubmission';
import { DeadlineTypeBadge, DeadlineType } from '@/components/jobs/DeadlineTypeBadge';
import { QuantityDistributionDisplay } from '@/components/jobs/QuantityDistributionDisplay';
import { useJobSpecifications, useRFQSpecificationQuantities } from '@/hooks/useJobSpecifications';
import { useSpecificationLocationQuantities } from '@/hooks/useSpecificationLocationQuantities';
import { useAuth } from '@/lib/auth';
import { SpecificationDataRenderer } from '@/components/specifications/SpecificationDataRenderer';
import { MatchScoreDisplay } from '@/components/rfq/MatchScoreDisplay';
import { SamplesProofsEditor } from '@/components/rfq/SamplesProofsEditor';
import { RFQAccessRestricted } from '@/components/rfq/RFQAccessRestricted';
import { useVendorVerificationStatus } from '@/hooks/useVendorVerificationStatus';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export default function RFQDetailPage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const { rfq, loading, error } = useRFQ(rfqId!);
  const { toast } = useToast();
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState<any[]>([]);
  const [loadingDelivery, setLoadingDelivery] = useState(false);

  // Check vendor verification status
  const { isVerified, loading: verificationLoading } = useVendorVerificationStatus();
  const isVendor = user?.role === 'vendor';
  const showRestricted = isVendor && !isVerified;

  // Fetch specifications and quantities
  const { specifications, loading: specsLoading } = useJobSpecifications(rfq?.job_id || '');
  const { quantities, loading: qtyLoading } = useRFQSpecificationQuantities(rfq?.id || '');
  
  // Fetch quantity distribution matrix
  const { 
    matrix, 
    getQuantity, 
    getSpecificationTotal, 
    getLocationTotal 
  } = useSpecificationLocationQuantities(rfq?.job_id || '');

  // Fetch match score for vendor
  const { matchScore, loading: matchScoreLoading } = usePublicRFQCriteria(rfq?.id);

  // Fetch delivery details
  useEffect(() => {
    const fetchDeliveryDetails = async () => {
      if (!rfq?.job_id) return;
      
      setLoadingDelivery(true);
      try {
        const { data, error } = await supabase
          .from('job_delivery_details')
          .select('*')
          .eq('job_id', rfq.job_id)
          .eq('is_product_destination', true)
          .order('delivery_order', { ascending: true });
        
        if (error) throw error;
        setDeliveryDetails(data || []);
      } catch (err) {
        console.error('Error fetching delivery details:', err);
      } finally {
        setLoadingDelivery(false);
      }
    };

    fetchDeliveryDetails();
  }, [rfq?.job_id]);

  // Debug logging
  useEffect(() => {
    console.log('RFQ Detail - rfqId (route param):', rfqId);
    console.log('RFQ Detail - rfq?.id (UUID):', rfq?.id);
    console.log('RFQ Detail - rfq?.job_id:', rfq?.job_id);
    console.log('Specifications:', specifications);
    console.log('Quantities:', quantities);
    console.log('Delivery Details:', deliveryDetails);
    
    // Track RFQ viewed for vendors
    if (rfq?.id && isVendor && isVerified) {
      analytics.track({
        name: ANALYTICS_EVENTS.RFQ_VIEWED,
        category: 'engagement',
        properties: {
          rfq_id: rfq.id,
          category: rfq.job?.category,
        },
      });
    }
  }, [rfqId, rfq, specifications, quantities, deliveryDetails, isVendor, isVerified]);

  const getTimeRemaining = (closesAt: string) => {
    const timeLeft = new Date(closesAt).getTime() - new Date().getTime();
    if (timeLeft <= 0) return 'Closed';
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const isQAFrozen = (closesAt: string) => {
    const timeLeft = new Date(closesAt).getTime() - new Date().getTime();
    return timeLeft <= 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  };

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a question',
        variant: 'destructive',
      });
      return;
    }

    if (!rfq) return;

    if (isQAFrozen(rfq.closes_at)) {
      toast({
        title: 'QNA_FREEZE_ACTIVE',
        description: 'Questions cannot be submitted within 24 hours of RFQ close',
        variant: 'destructive',
      });
      return;
    }

    setSubmittingQuestion(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: newQuestion, error } = await supabase
        .from('rfq_questions')
        .insert({
          rfq_id: rfq.id,
          question: question.trim(),
          asked_by_user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Note: Database trigger handles notification automatically

      toast({
        title: 'Question submitted',
        description: 'Your question has been submitted for review',
      });

      setQuestion('');
      // Refresh the page to show the new question
      window.location.reload();
    } catch (err) {
      console.error('Error submitting question:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to submit question',
        variant: 'destructive',
      });
    } finally {
      setSubmittingQuestion(false);
    }
  };


  // Show loading state while checking verification
  if (loading || verificationLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-8">Loading RFQ details...</div>
      </div>
    );
  }

  // Block unverified vendors from viewing RFQ details
  if (showRestricted) {
    return <RFQAccessRestricted />;
  }

  if (error || !rfq) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600 py-8">
          {error || 'RFQ not found'}
        </div>
      </div>
    );
  }

  const categoryLabel = rfq.job ? JOB_CATEGORIES.find(c => c.value === rfq.job.category)?.label : 'Unknown';
  const timeRemaining = getTimeRemaining(rfq.closes_at);
  const qaFrozen = isQAFrozen(rfq.closes_at);
  


  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/rfqs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to RFQs
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">{rfq.job?.title || 'RFQ Title'}</h1>
            <div className="flex items-center gap-2 sm:gap-4 text-sm sm:text-base text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Building className="h-4 w-4" />
                <span className="hidden sm:inline">Client Organization</span>
              </span>
              <Badge variant={rfq.status === 'open' ? 'default' : 'secondary'}>
                {rfq.status}
              </Badge>
              <span className="text-xs sm:text-sm">RFQ ID: {rfq.public_id}</span>
            </div>
          </div>
          
          <div className="text-left sm:text-right">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <span className="text-lg sm:text-xl font-bold">{timeRemaining}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Closes: {new Date(rfq.closes_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>


      <div className="grid grid-cols-1 gap-6">
        {/* Main Content */}
        <div className="space-y-6">
          {/* Match Score Section - Only show for vendors */}
          {isVendor && matchScore && !matchScore.details.no_criteria && (
            <MatchScoreDisplay matchScore={matchScore} compact={false} />
          )}


          {/* RFQ Details with Tabs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                RFQ Details
                <Badge variant={rfq.status === 'open' ? 'default' : 'secondary'}>
                  {rfq.status}
                </Badge>
              </CardTitle>
              <CardDescription>
                Review the RFQ requirements and submit your bid
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 p-4 sm:p-6">
              {/* Summary Stats */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${isVendor ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4`}>
                {!isVendor && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Budget</p>
                      <p className="font-medium">{formatCurrency(rfq.job?.budget_cents || 0)}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Due Date</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{rfq.job?.due_date ? new Date(rfq.job.due_date).toLocaleDateString() : '-'}</p>
                      {deliveryDetails[0]?.deadline_type && (
                        <DeadlineTypeBadge type={deliveryDetails[0].deadline_type as DeadlineType} />
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Time Left</p>
                    <p className="font-medium">{timeRemaining}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-purple-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{categoryLabel}</p>
                  </div>
                </div>
              </div>

              {/* Hard Deadline Warning */}
              {deliveryDetails[0]?.deadline_type === 'hard_deadline' && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <strong>Critical Deadline:</strong> This job has a hard deadline. Delivery must occur on or before the specified date. Late delivery may not be accepted by the client. Please factor this into your pricing and ensure you can meet this commitment before bidding.
                  </AlertDescription>
                </Alert>
              )}

              {/* Description */}
              <div>
                <h4 className="font-medium mb-2">Project Description</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{rfq.job?.description || 'No description available'}</p>
              </div>

              {/* Tabs for Specifications, Delivery, and Quantities */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Quantities and Delivery</h3>
                <Tabs defaultValue="specifications">
                  <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                    <TabsList className={`grid w-full ${deliveryDetails.length > 1 ? 'grid-cols-3 min-w-[500px]' : 'grid-cols-2 min-w-[350px]'} sm:min-w-0`}>
                      <TabsTrigger value="specifications" className="text-xs sm:text-sm">
                        <Package className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Specifications & Quantities</span>
                        <span className="sm:hidden">Specs</span>
                      </TabsTrigger>
                      <TabsTrigger value="delivery" className="text-xs sm:text-sm">
                        <MapPin className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Delivery Information</span>
                        <span className="sm:hidden">Delivery</span>
                      </TabsTrigger>
                      {deliveryDetails.length > 1 && (
                        <TabsTrigger value="quantities" className="text-xs sm:text-sm">
                          <Package className="h-4 w-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">Quantity Distribution</span>
                          <span className="sm:hidden">Qty Dist</span>
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </div>

                  <TabsContent value="specifications" className="mt-6">
                    {specsLoading || qtyLoading ? (
                      <div className="text-center py-4 text-muted-foreground">Loading specifications...</div>
                    ) : specifications.length > 0 ? (
                      <div className="space-y-4">
                        {specifications.map((spec) => {
                          const q = quantities.find((qq: any) => qq.specification_id === spec.id);
                          return (
                            <div key={spec.id} className="border rounded-lg p-4 space-y-3">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h5 className="font-medium">{spec.specification_name}</h5>
                                  <div className="flex gap-2 mt-1">
                                    <Badge variant="outline" className="capitalize">
                                      {spec.specification_type.replace('_', ' ')}
                                    </Badge>
                                    {spec.is_mandatory && (
                                      <Badge variant="destructive">
                                        Mandatory
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <span>Qty:</span>
                                    <Badge variant="secondary">{q?.quantity_option ?? '-'}</Badge>
                                    <span>Unit:</span>
                                    <Badge variant="secondary">{q?.unit ?? '-'}</Badge>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="pt-3 border-t">
                                <p className="text-sm text-muted-foreground mb-2">Specification Details:</p>
                                <div className="text-sm bg-muted p-3 rounded">
                                  <SpecificationDataRenderer content={spec.content ?? {}} specification={spec} />
                                </div>
                                {q?.notes && (
                                  <p className="mt-2 text-sm">
                                    <span className="text-muted-foreground">Notes:</span> {q.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No specifications available for this RFQ.</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="delivery" className="mt-6">
                    {loadingDelivery ? (
                      <div className="text-center py-4">Loading delivery information...</div>
                    ) : deliveryDetails.length > 0 ? (
                      <div className="space-y-4">
                        {/* Display Shipping Terms if available */}
                        {rfq.shipping_terms && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <MapPin className="h-5 w-5" />
                                Shipping Terms
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center gap-2 mb-3">
                                <Badge variant="secondary" className="text-base px-3 py-1">
                                  {rfq.shipping_terms === 'fob_origin' ? 'FOB Origin' : 'FOB Destination'}
                                </Badge>
                              </div>
                              <Alert>
                                <AlertDescription>
                                  {rfq.shipping_terms === 'fob_origin' ? (
                                    <>
                                      <strong>FOB Origin:</strong> The buyer takes ownership and responsibility for the goods once they leave the seller's location. The buyer is responsible for shipping costs, insurance, and any risk of loss or damage during transit.
                                    </>
                                  ) : (
                                    <>
                                      <strong>FOB Destination:</strong> The seller retains ownership and responsibility for the goods until they reach the buyer's location. The seller is responsible for shipping costs, insurance, and any risk of loss or damage during transit.
                                    </>
                                  )}
                                </AlertDescription>
                          </Alert>
                            </CardContent>
                          </Card>
                        )}

                        {/* Samples and Proofs Section */}
                        {specifications.length > 0 && rfq?.job_id && (
                          <SamplesProofsEditor
                            jobId={rfq.job_id}
                            specifications={specifications.map(s => ({ id: s.id, specification_name: s.specification_name }))}
                            readOnly={true}
                          />
                        )}

                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            {deliveryDetails.length === 1 ? 'Delivery Location' : `Delivery Locations (${deliveryDetails.length})`}
                          </h4>
                        </div>
                        
                        {deliveryDetails.map((delivery, index) => (
                          <Card key={delivery.id} className="border-2">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-lg">
                                <MapPin className="h-5 w-5" />
                                {deliveryDetails.length > 1 && <span className="text-muted-foreground">Location {index + 1}:</span>}
                                {delivery.delivery_name}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  {/* Delivery Address */}
                                  <div className="space-y-3">
                                    <h5 className="font-semibold text-sm">Address</h5>
                                    <div className="space-y-2 text-sm">
                                      <p>{delivery.address_line1}</p>
                                      {delivery.address_line2 && <p>{delivery.address_line2}</p>}
                                      <p>{delivery.city}, {delivery.state_province} {delivery.postal_code}</p>
                                      <p>{delivery.country}</p>
                                      {delivery.location_type && (
                                        <Badge variant="outline" className="capitalize mt-2">
                                          {delivery.location_type.replace('_', ' ')}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {/* Contact Information */}
                                  <div className="space-y-3">
                                    <h5 className="font-semibold text-sm">Contact Information</h5>
                                    <div className="space-y-2 text-sm">
                                      {delivery.contact_name && (
                                        <div>
                                          <Label className="text-muted-foreground">Name</Label>
                                          <p>{delivery.contact_name}</p>
                                        </div>
                                      )}
                                      {delivery.contact_phone && (
                                        <div>
                                          <Label className="text-muted-foreground">Phone</Label>
                                          <p>{delivery.contact_phone}</p>
                                        </div>
                                      )}
                                      {delivery.contact_email && (
                                        <div>
                                          <Label className="text-muted-foreground">Email</Label>
                                          <p>{delivery.contact_email}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Additional Details */}
                                {(delivery.preferred_delivery_date || (delivery.delivery_window_start && delivery.delivery_window_end)) && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                                    {delivery.preferred_delivery_date && (
                                      <div>
                                        <Label className="text-muted-foreground">Preferred Delivery Date</Label>
                                        <p className="text-sm">{new Date(delivery.preferred_delivery_date).toLocaleString()}</p>
                                      </div>
                                    )}
                                    {delivery.delivery_window_start && delivery.delivery_window_end && (
                                      <div>
                                        <Label className="text-muted-foreground">Delivery Window</Label>
                                        <p className="text-sm">
                                          {new Date(delivery.delivery_window_start).toLocaleString()} - {new Date(delivery.delivery_window_end).toLocaleString()}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Requirements & Instructions */}
                                {(delivery.special_instructions || delivery.access_requirements || delivery.dock_information) && (
                                  <div className="space-y-3 pt-4 border-t">
                                    <h5 className="font-semibold text-sm">Additional Information</h5>
                                    {delivery.special_instructions && (
                                      <div>
                                        <Label className="text-muted-foreground">Special Instructions</Label>
                                        <p className="text-sm whitespace-pre-wrap">{delivery.special_instructions}</p>
                                      </div>
                                    )}
                                    {delivery.access_requirements && (
                                      <div>
                                        <Label className="text-muted-foreground">Access Requirements</Label>
                                        <p className="text-sm whitespace-pre-wrap">{delivery.access_requirements}</p>
                                      </div>
                                    )}
                                    {delivery.dock_information && (
                                      <div>
                                        <Label className="text-muted-foreground">Dock Information</Label>
                                        <p className="text-sm whitespace-pre-wrap">{delivery.dock_information}</p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Delivery Hours */}
                                {delivery.delivery_hours && Object.keys(delivery.delivery_hours).length > 0 && (
                                  <div className="pt-4 border-t">
                                    <Label className="text-muted-foreground">Delivery Hours</Label>
                                    <div className="text-sm mt-2">
                                      <pre className="whitespace-pre-wrap font-mono text-xs bg-muted p-3 rounded">
                                        {JSON.stringify(delivery.delivery_hours, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {/* Carriers */}
                                {((delivery.preferred_carriers && delivery.preferred_carriers.length > 0) || 
                                  (delivery.restricted_carriers && delivery.restricted_carriers.length > 0)) && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                                    {delivery.preferred_carriers && delivery.preferred_carriers.length > 0 && (
                                      <div>
                                        <Label className="text-muted-foreground">Preferred Carriers</Label>
                                        <ul className="text-sm list-disc list-inside mt-1">
                                          {delivery.preferred_carriers.map((carrier: string, idx: number) => (
                                            <li key={idx}>{carrier}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {delivery.restricted_carriers && delivery.restricted_carriers.length > 0 && (
                                      <div>
                                        <Label className="text-muted-foreground">Restricted Carriers</Label>
                                        <ul className="text-sm list-disc list-inside mt-1">
                                          {delivery.restricted_carriers.map((carrier: string, idx: number) => (
                                            <li key={idx}>{carrier}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Delivery Requirements Flags */}
                                {(delivery.signature_required || delivery.id_required || delivery.appointment_required) && (
                                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                                    {delivery.signature_required && (
                                      <Badge variant="outline">Signature Required</Badge>
                                    )}
                                    {delivery.id_required && (
                                      <Badge variant="outline">ID Required</Badge>
                                    )}
                                    {delivery.appointment_required && (
                                      <Badge variant="outline">Appointment Required</Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No delivery information defined for this job.</p>
                      </div>
                    )}
                  </TabsContent>

                  {deliveryDetails.length > 1 && (
                    <TabsContent value="quantities" className="mt-6">
                      <QuantityDistributionDisplay
                        specifications={specifications}
                        deliveryLocations={deliveryDetails}
                        matrix={matrix}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </div>

              {/* Bid Submission */}
              <div className="border-t pt-6">
                <BidSubmission 
                  rfqId={rfq.id}
                  rfqStatus={rfq.status}
                  closesAt={rfq.closes_at}
                />
              </div>
            </CardContent>
          </Card>

          {/* Q&A Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Questions & Answers
                {qaFrozen && <Badge variant="outline">Q&A Frozen</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Submit Question Form */}
              {!qaFrozen && rfq.status === 'open' && (
                <form onSubmit={handleSubmitQuestion} className="mb-6">
                  <Label htmlFor="question">Ask a Question</Label>
                  <Textarea
                    id="question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Enter your question here..."
                    rows={3}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1 mb-3">
                    Questions are anonymous to other vendors
                  </p>
                  <Button type="submit" disabled={submittingQuestion}>
                    {submittingQuestion ? 'Submitting...' : 'Submit Question'}
                  </Button>
                </form>
              )}

              {qaFrozen && (
                <Alert className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Q&A period has ended. No new questions can be submitted within 24 hours of RFQ close.
                  </AlertDescription>
                </Alert>
              )}

              {/* Questions List */}
              {rfq.questions && rfq.questions.length > 0 ? (
                <div className="space-y-4">
                  {rfq.questions.map((q) => (
                    <div key={q.id} className="border rounded-lg p-4">
                      <div className="mb-2">
                        <p className="font-medium">Q: {q.question}</p>
                        <p className="text-sm text-muted-foreground">
                          Asked on {new Date(q.created_at).toLocaleString()}
                        </p>
                      </div>
                      {q.answer ? (
                        <div className="mt-3 p-3 bg-muted rounded">
                          <p><strong>A:</strong> {q.answer}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Answered on {q.answered_at ? new Date(q.answered_at).toLocaleString() : 'Unknown'}
                            {q.creates_spec_change && (
                              <Badge variant="outline" className="ml-2">Spec Change</Badge>
                            )}
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground italic mt-2">Awaiting answer...</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No questions submitted yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}